import { getRandomColor } from 'module/helpers/get-random-color';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { GroupHeadingProps } from 'module/ui/components/molecules/popover-select/group-heading';
import {
  HeadingCompact00,
  SingleLineEllipsis,
} from 'module/ui/components/styles';
import {
  faChevronDown,
  faChevronRight,
} from '@fortawesome/pro-regular-svg-icons';
import { Tag, Checkbox } from '@rocketlane/ui';
import { SourcePageTemplate } from 'api/custom-section/types';
import { PageTemplateLite } from 'api/page-template/types';
import { keyBy } from 'lodash';
import { useMemo, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { PageTemplateSourceSelector } from 'components/organisms/template-sources/source-selector';
import { useTemplatePageSources } from 'components/organisms/template-sources/use-sources';

const ListWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-02);
`;
const GroupHeadingLabel = styled.div`
  ${SingleLineEllipsis};
`;
const GroupHeadingIconWrapper = styled.div`
  padding: var(--spacing-01);
  display: flex;
  align-items: center;
  gap: var(--spacing-03);
`;
const HeadingWrapper = styled.div`
  ${HeadingCompact00};
  padding: var(--spacing-02) var(--spacing-03) var(--spacing-02)
    var(--spacing-05);
  height: 32px;
  display: flex;
  align-items: center;
  gap: var(--spacing-02);
`;
const CustomGroupHeading = ({
  onClick,
  data,
  key,
  active,
  value: currentSelected,
  setValue,
}: GroupHeadingProps) => {
  // Use Set for O(1) lookups
  const selectedSet = useMemo(
    () => new Set(currentSelected.map(({ value }) => value)),
    [currentSelected],
  );
  const optionSet = useMemo(
    () => new Set(data.options.map(({ value }) => value)),
    [data.options],
  );

  const isAllSelected = useMemo(
    () => data.options.every(({ value }) => selectedSet.has(value)),
    [data.options, selectedSet],
  );

  const handleCheckboxClick = useCallback(
    (e) => {
      e.stopPropagation();
      if (isAllSelected) {
        // Deselect all in this group
        setValue(currentSelected.filter(({ value }) => !optionSet.has(value)));
      } else {
        // Select all in this group (add missing)
        const newSelected = [
          ...currentSelected,
          ...data.options.filter(({ value }) => !selectedSet.has(value)),
        ];

        setValue(newSelected);
      }
    },
    [
      isAllSelected,
      setValue,
      currentSelected,
      optionSet,
      data.options,
      selectedSet,
    ],
  );

  return (
    <HeadingWrapper onClick={onClick} data-key={key}>
      <GroupHeadingIconWrapper>
        <Checkbox
          onClick={handleCheckboxClick}
          checked={isAllSelected}
          color="black"
        />
        <FontAwesomeIcon
          icon={active ? faChevronDown : faChevronRight}
          size={12}
        />
      </GroupHeadingIconWrapper>
      <GroupHeadingLabel>{data.label}</GroupHeadingLabel>
    </HeadingWrapper>
  );
};

const MaximumVisibleTags = 2;

interface Props {
  value?: SourcePageTemplate[];
  onChange?: (value: SourcePageTemplate[]) => void;
}
export const PeoplePageSourceDerivedQuestionSectionSourceSelector = ({
  value,
  onChange: onChangeProp,
}: Props) => {
  const { templatesByPage, isLoading: isTemplatePageLoading } =
    useTemplatePageSources();
  const allTemplatesById = useMemo(() => {
    return keyBy(Object.values(templatesByPage).flat(), 'templateId');
  }, [templatesByPage]);
  const valueForSelect = useMemo(() => {
    return (value ?? [])
      .map(({ templateId }) => allTemplatesById[templateId])
      .filter(Boolean);
  }, [allTemplatesById, value]);
  const selected = useMemo(() => {
    return isTemplatePageLoading ? (value ?? []) : valueForSelect;
  }, [isTemplatePageLoading, value, valueForSelect]);
  const allTemplatesSelected = useMemo(() => {
    return Object.values(allTemplatesById).length === selected.length;
  }, [allTemplatesById, selected.length]);
  const displayableValues = useMemo(() => {
    if (selected.length > MaximumVisibleTags) {
      return selected.slice(0, MaximumVisibleTags);
    }

    return selected;
  }, [selected]);

  const moreCount = useMemo(() => {
    return selected.length - displayableValues.length;
  }, [selected, displayableValues]);
  const onChange = useCallback(
    (next: PageTemplateLite[]) => {
      onChangeProp?.(
        next.map((template) => ({
          templateId: template.templateId,
          templateName: template.templateName,
          pageType: template.pageType,
          sourceType: template.sourceType,
        })),
      );
    },
    [onChangeProp],
  );

  return (
    <PageTemplateSourceSelector
      templatesByPage={templatesByPage}
      isLoading={isTemplatePageLoading}
      value={valueForSelect}
      onChange={onChange}
      checkboxColor="black"
      components={{
        GroupHeading: CustomGroupHeading,
      }}
      triggerContent={
        selected.length !== 0 && !allTemplatesSelected ? (
          <ListWrapper>
            {displayableValues.map(({ templateName, templateId }) => (
              <Tag
                key={templateName}
                color={getRandomColor(templateId) as any}
                variant="basic"
              >
                {templateName}
              </Tag>
            ))}
            {moreCount > 0 && (
              <Tag color="gray" variant="basic">
                <FormattedMessage
                  id="plus_more"
                  values={{ count: moreCount }}
                />
              </Tag>
            )}
          </ListWrapper>
        ) : (
          <Tag color="blue" variant="basic">
            <FormattedMessage id="rich_text_editor.sections.question_section.sources.placeholder" />
          </Tag>
        )
      }
    />
  );
};
